import type {HTMLChakraProps} from '@chakra-ui/react';
import {forwardRef, useEditableContext, useMergeRefs, useOutsideClick} from '@chakra-ui/react';
import {ChakraDiv} from '@linx-security/ui/utils/chakra-elements';
import type {RefObject} from 'react';
import {useRef} from 'react';

import {contains} from '../../utils/dom';

export type EditableRootProps = {
  closeOnEsc?: boolean;
  closeOnBlur?: boolean;
  closeOnClickOutside?: boolean;
} & HTMLChakraProps<'div'>;

export const EditableRoot = forwardRef<EditableRootProps, 'div'>((props, ref) => {
  const {closeOnEsc, closeOnBlur, closeOnClickOutside, ...rest} = props;
  const innerRef = useRef<HTMLDivElement>(null);
  const context = useEditableContext();
  const {onCancel, isEditing} = context;

  useOutsideClick({
    enabled: !!closeOnClickOutside && isEditing,
    ref: innerRef as RefObject<HTMLElement>,
    handler: () => onCancel(),
  });

  const onBlur: EditableRootProps['onBlur'] = event => {
    const doc = event.currentTarget.ownerDocument;
    const relatedTarget = (event.relatedTarget ?? doc.activeElement) as HTMLElement;
    const div = innerRef.current;

    if (!contains(div, relatedTarget)) {
      onCancel();
    }
  };

  const onKeyDown: EditableRootProps['onKeyDown'] = event => {
    if (event.key === `Escape`) {
      onCancel();
    }
  };

  const divRef = useMergeRefs(ref, innerRef);

  return (
    <ChakraDiv
      onKeyDown={closeOnEsc ? onKeyDown : undefined}
      onBlur={closeOnBlur ? onBlur : undefined}
      {...rest}
      ref={divRef}
    />
  );
});
